import type { ClientDatabase, Entity } from '@app/icare/client/db/schema'
import { DatabaseSchema, patientRelationTableNames } from '@app/icare/client/db/schema'
import { UserRoleEnum } from '@app/icare/entities/user/schema/User'
import type { FileRouteTypes } from '@app/icare/routeTree.gen'
import { isActivePatientRelation, isInactivePatientRelation } from '@app/icare/shared/api/form.type'
import { Authority } from '@app/icare/shared/auth/Authorities'
import { modulesOf, rolesOf } from '@app/icare/shared/auth/principal'
import type { AtLeast } from '@app/icare/types/helpers'
import type { MatchConditions, PureAbility } from '@casl/ability'
import { t } from '@lingui/core/macro'
import { abilityFactory } from '@pkg/auth/ability'
import { trimToUndefined } from '@pkg/shared/utils'

type CRUD = 'create' | 'read' | 'update' | 'delete' | 'restore'
type CrudWithAlias = CRUD | 'modify'

export type AppRoute = FileRouteTypes['fullPaths']
type AppSubjects = ClientDatabase & Entity & Record<AppRoute, unknown>

type CrudNameAbilities = {
  [K in keyof AppSubjects]: [CrudWithAlias, K]
}[keyof AppSubjects]
type CrudValueAbilities = {
  [K in keyof AppSubjects]: 'id' extends keyof AppSubjects[K] ? [CrudWithAlias, AtLeast<AppSubjects[K], 'id'>] : never
}[keyof AppSubjects]
export type AppAbilities = CrudNameAbilities | CrudValueAbilities
export type AppAbility = PureAbility<AppAbilities, MatchConditions>

export interface AppAbilityFactoryInput {
  session?: unknown
}

// Tables that are read-only this pass (admin-gated create/update deferred, no edit page, or a view).
const readOnlyTables = new Set<keyof ClientDatabase>(['cooperationPartner', 'user', 'article', 'patientFindingsView'])

export const appAbilityFactory = (opts: AppAbilityFactoryInput) => {
  const roles = rolesOf(opts.session)
  const modules = modulesOf(opts.session)
  const relationTables = new Set<string>(patientRelationTableNames)

  return abilityFactory<AppSubjects>(({ builder }) => {
    const { can, cannot } = builder

    // The whole app lives under the iCare module for now. Absent module → empty ability (default-deny).
    if (!modules.has('iCare')) {
      return
    }

    // Table-level baseline for non-relation tables.
    for (const name of Object.keys(DatabaseSchema) as (keyof ClientDatabase)[]) {
      if (relationTables.has(name)) {
        continue
      }
      can('read', name)
      if (readOnlyTables.has(name)) {
        continue
      }
      can('create', name)
      can('update', name)
    }

    // AD-teams are owned by Entra + the R-adteams migration: the name stays editable,
    // but they are never user-created and the Entra group is a fixed join key.
    cannot('create', 'adTeam').because(t`AD-Teams werden aus Entra übernommen und können nicht angelegt werden`)
    cannot('update', 'adTeam', 'entraGroupName').because(t`Die Entra-Gruppe ist fest und kann nicht geändert werden`)

    if (roles.has(UserRoleEnum.CARE_Aussendienst)) {
      can('read', '/my-patients')
      can('read', '/registered-patients')
    }
    can('read', '/notifications')
    can('read', '/orders')
    can('read', '/progress-reports')
    can('read', '/bia-measurements')
    can('read', '/signed')
    can('read', '/orders-stock')
    can('read', '/master-data-sheets')
    can('read', '/missing-data')

    // Tour-target routes (drive onboarding-tour eligibility via route-read).
    can('read', '/')
    can('read', '/patienten/')
    can('read', '/patienten/$id')
    can('read', '/patienten/$id/kostentraeger')

    // Patient field-level.
    // PLACEHOLDER status-transition policy — replace with the real role×status matrix.
    // TODO(status-matrix): exact transitions per role.
    cannot('update', 'patient', 'status', p => p.status?.value === 'closed').because(
      'Geschlossene Akten können nicht bearbeitet werden',
    )
    if (!roles.has(UserRoleEnum.CARE_Innendienst)) {
      cannot('update', 'patient', 'status').because(t`Nur Innendienst kann den Status ändern`)
    }
    if (!roles.has(UserRoleEnum.CARE_Aussendienst)) {
      cannot('update', 'patient', 'deceased.comment').because(t`Wird vom Aussendienst gepflegt`)
    }

    // Patient forms: approved forms are locked.
    cannot('update', 'patientForm', v => v.status === 'approved').because(
      'Abgeschlossene Formulare können nicht mehr bearbeitet werden',
    )

    // Patient relations.
    const isAdmin = roles.list.includes(Authority.iCARE_Admin)
    for (const rel of patientRelationTableNames) {
      can('read', rel)
      can('create', rel)
      if (rel === 'patientWeight') {
        can('update', rel, data => trimToUndefined(data.updatedAt as string | null | undefined) === undefined).because(
          'Nur neue Gewichtseinträge können bearbeitet werden',
        )
      } else {
        can('update', rel)
      }
      can('restore', rel)
      cannot('restore', rel, r => isActivePatientRelation(r)).because('Beziehung ist aktiv')
      cannot('update', rel, r => isInactivePatientRelation(r)).because('Beziehung ist inaktiv')
      if (isAdmin) {
        can('delete', rel)
      }
    }

    // Innendienst-only capabilities.
    if (!roles.has(UserRoleEnum.CARE_Innendienst)) {
      const innendienstManaged = t`Wird vom Innendienst gepflegt`
      // Cost-carrier exemption documents.
      cannot('update', 'patientCostCarrierExemption', 'activeFrom').because(innendienstManaged)
      cannot('update', 'patientCostCarrierExemption', 'activeTo').because(innendienstManaged)
      // Reviewing (verify/reject) an exemption writes verificationStatus.
      cannot('update', 'patientCostCarrierExemption', 'verificationStatus').because(innendienstManaged)
      // Patient notifications card.
      cannot('read', 'patientNotification').because(t`Nur Innendienst`)
    }
  }) as AppAbility
}

export const defaultAbility = appAbilityFactory({ session: undefined })
